import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Plus, Trash2, Printer, CalendarClock, ScanLine } from 'lucide-react';

const TIPI_IMPIANTO = ['Registratore di cassa', 'Stampante fiscale', 'Gestionale', 'POS', 'Bilancia', 'Altro'];

/** Step 3 wizard: impianti + abbonamenti opzionali. */
export default function WizardStepImpianti({ impianti, validPuntiVendita, onAdd, onUpdate, onRemove, onScan }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Aggiungi gli impianti (registratori, stampanti, gestionali) e attiva gli abbonamenti di assistenza.
        Puoi saltare questo passaggio se non necessario.
      </p>

      {impianti.map((impianto, index) => (
        <div key={impianto._uid || `imp-${index}`} className="border border-border p-4 space-y-3">
          <div className="flex justify-between items-center">
            <h4 className="font-bold text-sm flex items-center gap-2">
              <Printer className="h-4 w-4" />
              Impianto #{index + 1}
            </h4>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-none text-destructive"
              onClick={() => onRemove(index)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {validPuntiVendita.length > 0 && (
              <div className="md:col-span-3">
                <Label className="text-xs font-mono uppercase">Punto Vendita</Label>
                <Select
                  value={impianto.punto_vendita_index?.toString() || "none"}
                  onValueChange={(value) => onUpdate(index, 'punto_vendita_index', value === "none" ? null : parseInt(value))}
                >
                  <SelectTrigger className="rounded-none">
                    <SelectValue placeholder="Seleziona punto vendita" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="none">Non assegnato</SelectItem>
                    {validPuntiVendita.map((pv, pvIndex) => (
                      <SelectItem key={pv._uid || `pvopt-${pvIndex}`} value={pvIndex.toString()}>
                        {pv.nome} - {pv.citta}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs font-mono uppercase">Tipo *</Label>
              <Select
                value={impianto.tipo}
                onValueChange={(value) => onUpdate(index, 'tipo', value)}
              >
                <SelectTrigger className="rounded-none" data-testid={`wizard-impianto-tipo-${index}`}>
                  <SelectValue placeholder="Seleziona tipo" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {TIPI_IMPIANTO.map((tipo) => (
                    <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Matricola *</Label>
              <div className="flex gap-1">
                <Input
                  value={impianto.matricola}
                  onChange={(e) => onUpdate(index, 'matricola', e.target.value)}
                  className="rounded-none font-mono flex-1 min-w-0"
                  data-testid={`wizard-impianto-matricola-${index}`}
                />
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="rounded-none shrink-0 border-cyan-500 text-cyan-600"
                  onClick={() => onScan(index)}
                  title="Scansiona codice a barre: compila matricola, marca e modello dal magazzino"
                  data-testid={`wizard-impianto-scan-${index}`}
                >
                  <ScanLine className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Marca</Label>
              <Input
                value={impianto.marca}
                onChange={(e) => onUpdate(index, 'marca', e.target.value)}
                className="rounded-none"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Modello</Label>
              <Input
                value={impianto.modello}
                onChange={(e) => onUpdate(index, 'modello', e.target.value)}
                className="rounded-none"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Data Installazione</Label>
              <Input
                type="date"
                value={impianto.data_installazione}
                onChange={(e) => onUpdate(index, 'data_installazione', e.target.value)}
                className="rounded-none"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Scadenza Verifica</Label>
              <Input
                type="date"
                value={impianto.data_scadenza_verifica}
                onChange={(e) => onUpdate(index, 'data_scadenza_verifica', e.target.value)}
                className="rounded-none"
              />
            </div>
          </div>

          {/* Abbonamento Section */}
          <div className="border-t border-border pt-3 mt-3">
            <div className="flex items-center gap-2 mb-3">
              <Checkbox
                id={`abb-${index}`}
                checked={impianto.abbonamento_attivo}
                onCheckedChange={(checked) => onUpdate(index, 'abbonamento_attivo', checked)}
                data-testid={`wizard-impianto-abbonamento-${index}`}
              />
              <Label htmlFor={`abb-${index}`} className="text-sm font-medium flex items-center gap-2 cursor-pointer">
                <CalendarClock className="h-4 w-4" />
                Attiva abbonamento assistenza
              </Label>
            </div>

            {impianto.abbonamento_attivo && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pl-6">
                <div className="md:col-span-2">
                  <Label className="text-xs font-mono uppercase">Descrizione</Label>
                  <Input
                    value={impianto.abbonamento_descrizione}
                    onChange={(e) => onUpdate(index, 'abbonamento_descrizione', e.target.value)}
                    className="rounded-none"
                    placeholder={`Assistenza ${impianto.tipo || 'impianto'}`}
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Importo (EUR)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={impianto.abbonamento_importo}
                    onChange={(e) => onUpdate(index, 'abbonamento_importo', e.target.value)}
                    className="rounded-none"
                  />
                </div>
                <div></div>
                <div>
                  <Label className="text-xs font-mono uppercase">Data Inizio *</Label>
                  <Input
                    type="date"
                    value={impianto.abbonamento_data_inizio}
                    onChange={(e) => onUpdate(index, 'abbonamento_data_inizio', e.target.value)}
                    className="rounded-none"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Data Scadenza *</Label>
                  <Input
                    type="date"
                    value={impianto.abbonamento_data_scadenza}
                    onChange={(e) => onUpdate(index, 'abbonamento_data_scadenza', e.target.value)}
                    className="rounded-none"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        className="w-full rounded-none"
        onClick={onAdd}
        data-testid="wizard-add-impianto"
      >
        <Plus className="h-4 w-4 mr-2" />
        Aggiungi Impianto
      </Button>
    </div>
  );
}
